"use client"

import {
  useArtifactCanvas,
  PersonListCard,
  Badge,
  formatBriefDate,
  briefCandidateToPersonModel,
  talentLocationLine,
  talentTopSkills,
  cn,
} from '@yeshuman/sdk'
import { useCallback, useRef } from 'react'
import type { BriefCandidate } from '../../hooks/use-briefs'
import { TalentProfilePreviewCanvas } from './talent-profile-preview-canvas'
import { Calendar, MapPin } from 'lucide-react'

export type BriefSavedCandidateRowProps = {
  candidate: BriefCandidate
  className?: string
  variant?: 'default' | 'compact'
}

/** Saved talent on brief detail — composes {@link PersonListCard} with brief-specific slots. */
export function BriefSavedCandidateRow({
  candidate,
  className,
  variant = 'default',
}: BriefSavedCandidateRowProps) {
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const previewOpenKeyRef = useRef(0)
  const preview = candidate.profile_preview
  const personModel = briefCandidateToPersonModel(candidate)
  const displayName = personModel.name
  const locationLine = talentLocationLine(preview)
  const skills = talentTopSkills(preview, variant === 'compact' ? 3 : 5)
  const notes = candidate.notes?.trim()
  const isCompact = variant === 'compact'

  const openProfilePreview = useCallback(() => {
    previewOpenKeyRef.current += 1
    openCanvas({
      title: displayName,
      preventAutoFocus: true,
      content: (
        <TalentProfilePreviewCanvas
          key={`brief-saved-${candidate.profile_id}-${previewOpenKeyRef.current}`}
          profileId={candidate.profile_id}
          profileName={displayName}
          onClose={closeCanvas}
        />
      ),
    })
  }, [candidate.profile_id, closeCanvas, displayName, openCanvas])

  const body = (
    <div className="pointer-events-none space-y-2">
      {locationLine ? (
        <div
          className={cn(
            'flex items-center gap-2 text-sm text-muted-foreground',
            isCompact && 'text-xs',
          )}
        >
          <MapPin className="size-3.5 shrink-0" aria-hidden />
          <span>{locationLine}</span>
        </div>
      ) : null}
      {skills.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {skills.map((skill) => (
            <Badge key={skill} variant="secondarySoft" className="text-xs font-normal">
              {skill}
            </Badge>
          ))}
        </div>
      ) : null}
      {notes ? (
        <p
          className={cn(
            'line-clamp-3 rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm text-muted-foreground',
            isCompact && 'text-xs',
          )}
        >
          {notes}
        </p>
      ) : null}
    </div>
  )

  return (
    <PersonListCard
      variant={isCompact ? 'compact' : 'standard'}
      className={cn('h-full', className)}
      person={personModel}
      titleBadge={{ label: 'Saved', variant: 'secondarySoft' }}
      headerAside={
        <Badge variant="outline" className="shrink-0 text-xs font-normal">
          <Calendar className="mr-1 size-3" aria-hidden />
          {formatBriefDate(candidate.saved_at)}
        </Badge>
      }
      body={body}
      onOpen={openProfilePreview}
      openAriaLabel={`View saved profile for ${displayName}`}
    />
  )
}
